import { useSubscription, useLazyQuery, useApolloClient } from '@apollo/client';
import { AffectedCrewPayload, CrewMemberList, CrewUpdatedPayload, PersonaType, SearchCrewInput } from '@bechtel/types';
import { useEffect, useState } from 'react';
import {
  SUBSCRIPTION_CREW_UPDATE,
  GET_CREW_HIERARCHY,
  GET_CREW_MEMBERS_WITH_CREW,
  GET_CREW_MEMBERS_CREW_CHANGE_REQUEST,
} from '@bechtel/client-gql';
import { forEach } from 'lodash';

export function useCrewUpdateSubscription() {
  const client = useApolloClient();
  const { data } = useSubscription<{ crewUpdated: CrewUpdatedPayload }>(SUBSCRIPTION_CREW_UPDATE);
  const [getGenralForemanList] = useLazyQuery<{ crewMembers: CrewMemberList }, { input: SearchCrewInput }>(
    GET_CREW_HIERARCHY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  );
  const [getForemanList] = useLazyQuery<{ crewMembers: CrewMemberList }, { input: SearchCrewInput }>(
    GET_CREW_MEMBERS_WITH_CREW,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  );
  const [getCraftList] = useLazyQuery<{ crewMembers: CrewMemberList }, { input: SearchCrewInput }>(
    GET_CREW_MEMBERS_CREW_CHANGE_REQUEST,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  );

  const refetchAffectedCrewMembers = (affectedCrewMembers: AffectedCrewPayload[]) => {
    forEach(affectedCrewMembers, async (affectedCrewMember) => {
      if (affectedCrewMember.personaTypeId === PersonaType.SUPERINTENDENT) {
        const { data: gfData } = await getGenralForemanList({
          variables: {
            input: {
              superintendentId: affectedCrewMember.hierarchy.superintendentId,
            },
          },
        });
        if (gfData?.crewMembers && gfData.crewMembers.items && gfData.crewMembers.items.length > 0) {
          client.cache.writeQuery({
            query: GET_CREW_MEMBERS_CREW_CHANGE_REQUEST,
            variables: {
              input: {
                superintendentId: affectedCrewMember.hierarchy.superintendentId,
              },
            },
            data: {
              crewMembers: {
                cursor: 1,
                items: gfData.crewMembers.items,
                total: gfData.crewMembers.items.length,
              },
            },
          });
        }
      } else if (affectedCrewMember.personaTypeId === PersonaType.GENERAL_FOREMAN) {
        const { data: foremanData } = await getForemanList({
          variables: {
            input: {
              superintendentId: affectedCrewMember.hierarchy.superintendentId,
              generalForemanId: affectedCrewMember.hierarchy.generalForemanId,
            },
          },
        });
        if (foremanData?.crewMembers && foremanData.crewMembers.items && foremanData.crewMembers.items.length > 0) {
          client.cache.writeQuery({
            query: GET_CREW_MEMBERS_CREW_CHANGE_REQUEST,
            variables: {
              input: {
                superintendentId: affectedCrewMember.hierarchy.superintendentId,
                generalForemanId: affectedCrewMember.hierarchy.generalForemanId,
              },
            },
            data: {
              crewMembers: {
                cursor: 1,
                items: foremanData.crewMembers.items,
                total: foremanData.crewMembers.items.length,
              },
            },
          });
        }
      } else {
        const { data: craftsData } = await getCraftList({
          variables: {
            input: {
              superintendentId: affectedCrewMember.hierarchy.superintendentId,
              generalForemanId: affectedCrewMember.hierarchy.generalForemanId,
              foremanId: affectedCrewMember.hierarchy.foremanId,
            },
          },
        });
        if (craftsData?.crewMembers && craftsData.crewMembers.items && craftsData.crewMembers.items.length > 0) {
          client.cache.writeQuery({
            query: GET_CREW_MEMBERS_CREW_CHANGE_REQUEST,
            variables: {
              input: {
                superintendentId: affectedCrewMember.hierarchy.superintendentId,
                generalForemanId: affectedCrewMember.hierarchy.generalForemanId,
                foremanId: affectedCrewMember.hierarchy.foremanId,
              },
            },
            data: {
              crewMembers: {
                cursor: 1,
                items: craftsData.crewMembers.items,
                total: craftsData.crewMembers.items.length,
              },
            },
          });
        }
      }
    });
  };

  const response = useState<AffectedCrewPayload[]>([]);
  const setAffectedCrewMembers = response[1];

  useEffect(() => {
    if (
      !data ||
      !data.crewUpdated ||
      !data.crewUpdated.affectedCrewMembers ||
      data.crewUpdated.affectedCrewMembers.length == 0
    )
      return;
    setAffectedCrewMembers(data.crewUpdated.affectedCrewMembers);
    refetchAffectedCrewMembers(data.crewUpdated.affectedCrewMembers);
  }, [data]);

  return response;
}
